/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import { Stack, Typography, Paper, Grid, Box, styled } from '@mui/material';
import { doc, getDoc } from 'firebase/firestore';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { db } from '../../../../../../firebase.config';

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: (theme.vars ?? theme).palette.text.secondary,
  ...theme.applyStyles('dark', { backgroundColor: '#1A2027' }),
}));

export default function SumV2({ campagnId, mailContent, campagnData }) {
  const [loading, setLoading] = useState(false);
  const [event, setEvent] = useState(null);       // null au lieu de {}
  const [audiences, setAudiences] = useState([]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        setLoading(true);
        // reset
        if (isMounted) setAudiences([]);

        // ---- Récup event ----
        if (campagnData?.eventID) {
          const eventRef = doc(db, 'evenement', campagnData.eventID);
          const eventSnap = await getDoc(eventRef);
          if (eventSnap.exists() && isMounted) {
            // on stocke directement les données (pas un snapshot)
            setEvent({ id: eventSnap.id, ...eventSnap.data() });
          }
        } else if (isMounted) {
          setEvent(null);
        }

        // ---- Récup populations ----
        if (Array.isArray(campagnData?.audiences) && campagnData.audiences.length > 0) {
          const reads = campagnData.audiences.map(async (popId) => {
            const ref = doc(db, `organisations/${campagnData.organisationId}/crm/${campagnData.organisationId}/populations`, String(popId));
            const snap = await getDoc(ref);
            return snap.exists() ? { id: snap.id, ...snap.data() } : null;
          });
          const results = (await Promise.all(reads)).filter(Boolean);
          if (isMounted) setAudiences(results);
        }
      } catch (error) {
        console.error(error);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, [campagnData]);

  const hasPromoEmail = campagnData?.campagneType === 0;
  const audienceTypeAll = campagnData?.audienceType === 0;
  const audienceTypePop = campagnData?.audienceType === 1;

  return (
    <Box paddingBottom={10} sx={{ width: '100%' }} padding={2}>
      <Stack spacing={2} padding={2}>
        <Stack spacing={4}>
          <Grid container spacing={2} alignItems="stretch">
            {/* Colonne gauche */}
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                {/* Audience */}
                <Box sx={{ bgcolor: 'white', border: '2px solid #f2f2f2', borderRadius: '7px', p: 2 }}>
                  <Typography variant="subtitle1">Audience</Typography>
                  <Stack spacing={3} p={2}>
                    <Stack spacing={3} pt={3}>
                      <Stack spacing={1}>
                        <Typography variant="subtitle2">Type d'email</Typography>
                        <Stack my={2} direction="row" spacing={1}>
                          <Box
                            sx={
                              campagnData?.campagneType === 0
                                ? { bgcolor: '#F5EFFA', border: '2px solid #5E17EB', borderRadius: '10px', cursor: 'pointer' }
                                : { bgcolor: '#f2f2f2', borderRadius: '10px', cursor: 'pointer' }
                            }
                            px={2}
                            py={1.25}
                          >
                            <Typography variant="body2">Email promotionnel</Typography>
                          </Box>
                          <Box
                            sx={
                              campagnData?.campagneType === 1
                                ? { bgcolor: '#F5EFFA', border: '2px solid #5E17EB', borderRadius: '10px', cursor: 'pointer' }
                                : { bgcolor: '#f2f2f2', borderRadius: '10px', cursor: 'pointer' }
                            }
                            px={2}
                            py={1.25}
                          >
                            <Typography variant="body2">Informations pratiques</Typography>
                          </Box>
                        </Stack>
                      </Stack>

                      {/* Bloc événement si campagne informative */}
                      {campagnData?.campagneType === 1 && (
                        <Stack spacing={1}>
                          <Typography variant="body1"><strong>Événement</strong></Typography>

                          {event ? (
                            <Stack direction="row" spacing={1} component="li" alignItems="center">
                              {event?.idVisuel ? (
                                <LazyLoadImage
                                  src={event.idVisuel}
                                  alt={event.nomEvent || 'visuel événement'}
                                  className="img-lazy"
                                  width={70}
                                  height={70}
                                  style={{ objectFit: 'cover' }}
                                  effect="blur"
                                />
                              ) : (
                                <Box sx={{ width: 70, height: 70, bgcolor: '#eee', borderRadius: 1 }} />
                              )}
                              <Box>
                                <Typography variant="subtitle1">{event?.nomEvent || '—'}</Typography>
                                {/* <Typography variant="body2" color="textSecondary">{getTimeDifference(event.dateDebut)}</Typography> */}
                                {typeof event?.participant === 'number' && (
                                  <Typography variant="body2" color="textSecondary">
                                    {event.participant} participants
                                  </Typography>
                                )}
                              </Box>
                            </Stack>
                          ) : (
                            <Box sx={{ p: 2, bgcolor: '#FFADB0', borderRadius: '10px' }}>
                              <Typography variant="body2">Aucun événement disponible</Typography>
                            </Box>
                          )}
                        </Stack>
                      )}
                    </Stack>

                    {/* Détails audience pour Email promotionnel */}
                    {hasPromoEmail && (
                      <>
                        {audienceTypeAll ? (
                          <Box p={2}>
                            <Typography variant="body2">
                              Ces mails seront envoyés à vos abonnés newsletter uniquement.
                            </Typography>
                          </Box>
                        ) : audienceTypePop ? (
                          <Stack direction="row" px={2} spacing={1} flexWrap="wrap">
                            {audiences.map((pop) => (
                              <Box key={pop.id} px={1.5} py={0.5} sx={{ bgcolor: '#f2f2f2', borderRadius: '50px' }}>
                                <Typography variant="caption">{pop?.name ?? pop?.id ?? 'Population'}</Typography>
                              </Box>
                            ))}
                          </Stack>
                        ) : null}
                      </>
                    )}
                  </Stack>
                </Box>

                {/* Sujet */}
                <Box sx={{ bgcolor: 'white', border: '2px solid #f2f2f2', borderRadius: '7px', p: 2 }}>
                  <Typography variant="subtitle1">Sujet</Typography>
                  <Stack spacing={3} p={2}>
                    <Typography variant="body2">{campagnData?.object || '—'}</Typography>
                  </Stack>
                </Box>

                {/* Adresse de réponse */}
                <Box sx={{ bgcolor: 'white', border: '2px solid #f2f2f2', borderRadius: '7px', p: 2 }}>
                  <Typography variant="subtitle1">Adresse de réponse</Typography>
                  <Stack spacing={3} p={2}>
                    <Typography variant="body2">{campagnData?.sender?.name || '—'}</Typography>
                  </Stack>
                </Box>
              </Box>
            </Grid>

            {/* Colonne droite : Preview */}
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <Box sx={{
                bgcolor: 'white',
                border: '2px solid #f2f2f2',
                borderRadius: '7px',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}>
                <Stack justifyContent="center" sx={{ flex: 1, minHeight: 0 }}>
                  <Box sx={{ flex: 1, overflow: 'auto' }}>
                    <iframe
                      title={
                        typeof mailContent?.data === 'function'
                          ? (mailContent.data()?.titre ?? 'Aperçu e-mail')
                          : (mailContent?.titre ?? 'Aperçu e-mail')
                      }
                      srcDoc={
                        typeof mailContent?.data === 'function'
                          ? (mailContent.data()?.html ?? '')
                          : (mailContent?.html ?? '')
                      }
                      style={{ width: '100%', height: '100%', border: 'none' }}
                    />
                  </Box>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Stack>
      </Stack>
    </Box>
  );
}
